/**
 * Auth hook — manages JWT tokens, login, logout, and auto-refresh.
 *
 * Clean MT model: username/password login, no org/invite, no registration.
 * Admin creates accounts via the admin panel.
 *
 * Stores tokens in localStorage. Automatically refreshes the access
 * token 2min before expiry. Used by AuthContext.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { User, LoginResponse, RefreshResponse, SessionInitResponse } from '@galactus/shared';

const STORAGE_PREFIX = 'g1xl_mt_';
const ACCESS_TOKEN_KEY = `${STORAGE_PREFIX}access_token`;
const REFRESH_TOKEN_KEY = `${STORAGE_PREFIX}refresh_token`;
const USER_KEY = `${STORAGE_PREFIX}user`;

const REFRESH_BUFFER_MS = 2 * 60 * 1000;

function getRelayBaseUrl(): string {
  const env = (import.meta.env.VITE_RELAY_HTTP_URL as string | undefined)?.trim();
  if (env) {
    if (env.startsWith('http')) {
      return new URL(env).origin;
    }
    return window.location.origin;
  }
  return window.location.origin;
}

function parseJwtExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  isLoading: boolean;
  error: string | null;
  sessionInit: SessionInitResponse | null;
  sessionInitError: string | null;
}

export interface AuthActions {
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshSessionInit: () => Promise<SessionInitResponse | null>;
}

export function useAuth(): AuthState & AuthActions {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const stored = localStorage.getItem(USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [accessToken, setAccessToken] = useState<string | null>(() =>
    localStorage.getItem(ACCESS_TOKEN_KEY)
  );

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionInit, setSessionInit] = useState<SessionInitResponse | null>(null);
  const [sessionInitError, setSessionInitError] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const baseUrl = getRelayBaseUrl();

  const storeTokens = useCallback(
    (tokens: { accessToken: string; refreshToken: string }, userData: User) => {
      localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
      setAccessToken(tokens.accessToken);
      setUser(userData);
    },
    []
  );

  const clearTokens = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setAccessToken(null);
    setUser(null);
  }, []);

  const refreshSessionInit = useCallback(async (): Promise<SessionInitResponse | null> => {
    try {
      const res = await fetch(`${baseUrl}/relay/session-init?_=${Date.now()}`);
      if (!res.ok) {
        throw new Error(`Relay init check failed (${res.status})`);
      }

      const data = (await res.json()) as SessionInitResponse;
      setSessionInit(data);
      setSessionInitError(null);
      return data;
    } catch (err) {
      setSessionInit(null);
      setSessionInitError(err instanceof Error ? err.message : 'Failed to reach relay');
      return null;
    }
  }, [baseUrl]);

  const scheduleRefresh = useCallback(
    (token: string) => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      const exp = parseJwtExp(token);
      if (!exp) return;

      const delay = Math.max(exp - Date.now() - REFRESH_BUFFER_MS, 5000);
      refreshTimerRef.current = setTimeout(async () => {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken) return;

        try {
          const res = await fetch(`${baseUrl}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          });

          if (!res.ok) {
            clearTokens();
            return;
          }

          const data = (await res.json()) as RefreshResponse;
          localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
          setAccessToken(data.accessToken);
          scheduleRefresh(data.accessToken);
        } catch {
          // Network error — will retry on next user action
        }
      }, delay);
    },
    [baseUrl, clearTokens]
  );

  useEffect(() => {
    void refreshSessionInit();
  }, [refreshSessionInit]);

  useEffect(() => {
    if (accessToken) {
      scheduleRefresh(accessToken);
    }
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [accessToken, scheduleRefresh]);

  const login = useCallback(
    async (username: string, password: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetch(`${baseUrl}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Login failed');
        }

        const data = (await res.json()) as LoginResponse;
        storeTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken }, data.user);
        scheduleRefresh(data.accessToken);
        await refreshSessionInit();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Login failed');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [baseUrl, storeTokens, scheduleRefresh, refreshSessionInit]
  );

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      try {
        await fetch(`${baseUrl}/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
      } catch {
        // Best-effort server-side logout
      }
    }
    setSessionInitError(null);
    setError(null);
    clearTokens();
  }, [baseUrl, clearTokens]);

  const clearError = useCallback(() => setError(null), []);

  return {
    user,
    accessToken,
    isLoading,
    error,
    sessionInit,
    sessionInitError,
    login,
    logout,
    clearError,
    refreshSessionInit,
  };
}
